import { useState, useMemo } from 'react';
import { useReportData } from '../hooks/useReportData';
import { ReportPieChart } from '../components/report/ReportPieChart';
import { ReportLineChart } from '../components/report/ReportLineChart';
import { ReportDrillDown } from '../components/report/ReportDrillDown';
import { ReportSection } from '../components/report/ReportSection';
import { KpiCard } from '../components/KpiCard';
import { ContextualHelp } from '../components/ContextualHelp';
import { fmtEur2, fmtJours } from '../components/report/formatters';
import type { ReportParams } from '../types/widget';

interface PieRow { tranche: string; tri: number; nb_factures: number; montant_ht: number; delai_moyen_jours: number }
interface CourbeRow { mois: string; nb_factures: number; delai_moyen_jours: number; delai_min: number; delai_max: number }

export function DelaisEnregistrement({ filters }: { filters: ReportParams }) {
    const [drillTarget, setDrillTarget] = useState<{ type: 'prestataire'; tranche?: string; mois?: string } | null>(null);

    const piePrestataire = useReportData<PieRow>('/api/emis/delais-enregistrement/pie-prestataire', filters);
    const courbe = useReportData<CourbeRow>('/api/emis/delais-enregistrement/courbe', filters);

    // KPIs basés sur les données prestataire uniquement
    const kpis = useMemo(() => {
        const rows = piePrestataire.data;
        if (!rows.length) return null;
        const nbPrestataire = rows.reduce((s, r) => s + Number(r.nb_factures), 0);
        const montantTotal = rows.reduce((s, r) => s + Number(r.montant_ht), 0);
        const delaiMoyenP = nbPrestataire > 0
            ? rows.reduce((s, r) => s + Number(r.delai_moyen_jours) * Number(r.nb_factures), 0) / nbPrestataire
            : null;
        return { nbPrestataire, montantTotal, delaiMoyenPrestataire: delaiMoyenP };
    }, [piePrestataire.data]);

    const toPieDataVolume = (rows: PieRow[]) =>
        rows.map((r) => ({
            name: `${r.tranche} (${r.nb_factures})`,
            value: Number(r.nb_factures),
            sort_order: r.tri,
        }));

    const toPieDataMontant = (rows: PieRow[]) =>
        rows.map((r) => ({
            name: `${r.tranche} (${fmtEur2(Number(r.montant_ht))})`,
            value: Number(r.montant_ht),
            sort_order: r.tri,
        }));

    const courbeData = useMemo(() => {
        const months = courbe.data.map((r) => r.mois);
        return {
            months,
            series: [
                { name: 'Délai moyen', data: courbe.data.map((r) => r.delai_moyen_jours) },
                { name: 'Min', data: courbe.data.map((r) => r.delai_min), lineStyle: 'dashed' as const, color: '#10b981' },
                { name: 'Max', data: courbe.data.map((r) => r.delai_max), lineStyle: 'dashed' as const, color: '#ef4444' },
            ],
        };
    }, [courbe.data]);

    const drillEndpoint = '/api/emis/delais-enregistrement/drill-down-prestataire';

    const drillParams = useMemo(
        () => ({ ...filters, tranche: drillTarget?.tranche, mois: drillTarget?.mois }),
        [filters, drillTarget],
    );

    const drillColumns = [
        { key: 'fournisseur', label: 'Fournisseur', filterable: true },
        { key: 'numero_facture', label: 'N° facture', filterable: true },
        { key: 'libelle', label: 'Libellé', filterable: true },
        { key: 'date_facture', label: 'Date facture' },
        { key: 'date_enregistrement', label: 'Enregistrement' },
        { key: 'montant_ht', label: 'Montant HT', align: 'right' as const, format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant_ht', formatTotal: (v: number) => fmtEur2(v) },
        { key: 'delai_jours', label: 'Délai (j)', align: 'right' as const },
        { key: 'tranche', label: 'Tranche', filterable: true },
    ];

    // Tranches ordonnées pour navigation pie
    const tranchesPrestataire = useMemo(
        () => [...piePrestataire.data].sort((a, b) => a.tri - b.tri).map((r) => r.tranche),
        [piePrestataire.data],
    );

    const handleTrancheChange = (tranche: string) => {
        if (drillTarget) setDrillTarget({ ...drillTarget, tranche, mois: undefined });
    };

    // Extraction de la tranche depuis le label du pie : on match ce qui précède " (" final
    const extractTranche = (name: string) => name.replace(/ \(.+\)$/, '');

    return (
        <div className="space-y-6">
            {/* Cartouche de définition métier (#33) */}
            <ContextualHelp
                ariaLabel="Définition du délai d'enregistrement"
                summary={
                    <p>
                        <strong>Délai d'enregistrement :</strong> période maximale entre la
                        réception d'une facture émise par un prestataire et sa saisie dans la
                        comptabilité.
                    </p>
                }
                detailsTitle="Délai d'enregistrement"
                details={
                    <p>
                        Période maximale entre la réception d'une facture émise par un
                        prestataire et sa saisie dans la comptabilité.
                    </p>
                }
            />

            {/* KPI Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard label="Factures prestataires" value={kpis?.nbPrestataire ?? '—'} loading={piePrestataire.loading} />
                <KpiCard
                    label="Montant total prestataires"
                    value={kpis?.montantTotal != null ? fmtEur2(kpis.montantTotal) : '—'}
                    loading={piePrestataire.loading}
                />
                <KpiCard
                    label="Délai moyen prestataire"
                    value={kpis?.delaiMoyenPrestataire != null ? `${Math.round(kpis.delaiMoyenPrestataire)}` : '—'}
                    unit="jours"
                    loading={piePrestataire.loading}
                />
                <KpiCard label="Mois couverts" value={courbe.data.length || '—'} loading={courbe.loading} />
            </div>

            {/* Deux camemberts Prestataire côte à côte : Nombre de factures + Montant */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ReportSection title="Prestataire — Nombre de factures" loading={piePrestataire.loading} error={piePrestataire.error}>
                    <ReportPieChart
                        title="Délais d'enregistrement Prestataire — Nombre de factures"
                        data={toPieDataVolume(piePrestataire.data)}
                        height={300}
                        onSliceClick={(name) => setDrillTarget({ type: 'prestataire', tranche: extractTranche(name) })}
                    />
                </ReportSection>
                <ReportSection title="Prestataire — Montant" loading={piePrestataire.loading} error={piePrestataire.error}>
                    <ReportPieChart
                        title="Délais d'enregistrement Prestataire — Montant"
                        data={toPieDataMontant(piePrestataire.data)}
                        height={300}
                        valueFormatter={fmtEur2}
                        onSliceClick={(name) => setDrillTarget({ type: 'prestataire', tranche: extractTranche(name) })}
                    />
                </ReportSection>
            </div>

            {/* Drill-down tranche — entre les pies et la courbe */}
            {drillTarget?.tranche && (
                <ReportDrillDown
                    title={`Factures prestataire — ${drillTarget.tranche}`}
                    endpoint={drillEndpoint}
                    params={drillParams}
                    enabled={true}
                    onClose={() => setDrillTarget(null)}
                    columns={drillColumns}
                    tranches={tranchesPrestataire}
                    currentTranche={drillTarget.tranche}
                    onTrancheChange={handleTrancheChange}
                />
            )}

            {/* Courbe */}
            <ReportSection title="Évolution mensuelle (prestataires)" loading={courbe.loading} error={courbe.error}>
                <ReportLineChart
                    title="Délai moyen d'enregistrement par mois"
                    categories={courbeData.months}
                    series={courbeData.series}
                    valueFormatter={fmtJours}
                    onPointClick={(_, mois) => setDrillTarget({ type: 'prestataire', mois })}
                />
                {/* Drill-down mois — sous la courbe */}
                {drillTarget?.mois && (
                    <ReportDrillDown
                        title={`Factures prestataire — ${drillTarget.mois}`}
                        endpoint={drillEndpoint}
                        params={drillParams}
                        enabled={true}
                        onClose={() => setDrillTarget(null)}
                        columns={drillColumns}
                    />
                )}
            </ReportSection>
        </div>
    );
}
